import { useMemo } from "react";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useTasksByType } from "@coworker/apprestructured/src/task-management/services/useTasksByType";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";

/**
 * Composite key used to look up a pending task by (question_id, area).
 * Must be used consistently when building and querying the pendingTaskByKey map.
 */
export function mfaqTaskKey(questionId: string, area: string): string {
  return `${questionId}::${area}`;
}

interface MfaqTaskDetails {
  question_id?: string;
  question_area?: string;
}

/**
 * Returns a lookup map built from pending (non-completed, non-closed)
 * MFAQ follow-up tasks fetched from tasks-v2:
 *
 * - pendingTaskByKey: mfaqTaskKey(question_id, area) → Task
 *   Used by MfaqTaskButtonComponent to decide "Show task" vs "Create task"
 *   for the specific area currently selected, and by Questions to show the
 *   orange flag for the selected area.
 *
 * The underlying query is keyed under TASKS_V2_QUERY_KEY so React Query
 * automatically invalidates it when a task is created or updated.
 *
 * Pass `enabled: false` to disable fetching (e.g. when the V2 flag is off)
 * so that no requests are made to the tasks-v2 backend.
 *
 * Note: useAllTaskTypes() is called unconditionally because it has
 * staleTime: Infinity and is loaded once for the entire app session —
 * it is already fetched by other components (QuickActions, MfaqTaskButtonComponent)
 * regardless of this hook, so no extra network cost is incurred.
 */
export function usePendingMfaqTasks({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const storeId = useStoreId();
  const { data: taskTypes } = useAllTaskTypes();

  const mfaqTaskTypeId = useMemo(
    () =>
      taskTypes?.find(({ name }) => name === TaskTypeNames.MFAQ_FOLLOW_UP)?.id,
    [taskTypes]
  );

  const { data: tasks = [] } = useTasksByType(
    enabled ? mfaqTaskTypeId : null,
    enabled ? storeId : null,
    enabled ? "pending" : null
  );

  const pendingTaskByKey = useMemo(() => {
    const map = new Map<string, Task>();
    for (const task of tasks) {
      const { question_id: questionId, question_area: area } =
        task.details as MfaqTaskDetails;
      if (questionId && area) {
        // Keep the most recently created task per (question, area) tuple.
        const key = mfaqTaskKey(questionId, area);
        const existing = map.get(key);
        if (!existing || task.created_at > existing.created_at) {
          map.set(key, task);
        }
      }
    }
    return map;
  }, [tasks]);

  return { pendingTaskByKey };
}
